import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { map, switchMap, tap, catchError, take } from 'rxjs/operators';
import firebase from 'firebase/compat/app';
import { AccountDeletionService } from '../../services/account-deletion.service';

export interface DeletionRequest {
  id: string;
  userId: string;
  reason: string;
  contactEmail: string;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  createdAt: firebase.firestore.Timestamp;
  updatedAt: firebase.firestore.Timestamp;
  userName?: string;
  userEmail?: string;
  userPhoneNumber?: string;
}

export interface AdminUser {
  uid: string;
  email: string;
  isAdmin: boolean;
  displayName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private auth = new FirebaseTSAuth();
  private adminUser = new BehaviorSubject<AdminUser | null>(null);
  currentAdmin = this.adminUser.asObservable();

  constructor(
    private db: AngularFirestore,
    private afAuth: AngularFireAuth,
    private accountDeletionService: AccountDeletionService
  ) {
    // Vérifier le statut d'authentification au démarrage
    this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          // Vérifier si l'utilisateur a le champ LenaliSuperAdminAccount à true
          return this.checkIfUserIsAdmin(user.uid).pipe(
            switchMap(isAdmin => {
              if (isAdmin) {
                return this.db.doc<AdminUser>(`admins/${user.uid}`).valueChanges().pipe(
                  map(adminData => {
                    if (adminData) {
                      return { ...adminData, uid: user.uid, email: user.email };
                    } else {
                      // Si l'utilisateur n'existe pas dans la collection admins, le créer
                      const newAdmin: AdminUser = {
                        uid: user.uid,
                        email: user.email,
                        isAdmin: true,
                        displayName: user.displayName || 'Admin'
                      };
                      this.db.doc(`admins/${user.uid}`).set(newAdmin);
                      return newAdmin;
                    }
                  })
                );
              } else {
                // Déconnexion si l'utilisateur n'est pas un administrateur
                this.afAuth.signOut();
                return of(null);
              }
            })
          );
        } else {
          return of(null);
        }
      })
    ).subscribe(admin => {
      this.adminUser.next(admin);
    });
  }

  /**
   * Vérifier si un utilisateur est administrateur en cherchant le champ LenaliSuperAdminAccount
   */
  private checkIfUserIsAdmin(userId: string): Observable<boolean> {
    return this.db.doc(`Users/${userId}`).get().pipe(
      map(doc => {
        if (!doc.exists) return false;
        
        const userData = doc.data();
        // Vérifier si le champ LenaliSuperAdminAccount existe et est défini à true
        return userData && userData['LenaliSuperAdminAccount'] === true;
      }),
      catchError(error => {
        console.error('Erreur lors de la vérification des droits administrateur:', error);
        return of(false);
      })
    );
  }

  /**
   * Connecter un administrateur
   */
  async adminLogin(email: string, password: string): Promise<boolean> {
    try {
      // Connecter l'utilisateur avec email/mot de passe
      const result = await this.afAuth.signInWithEmailAndPassword(email, password);
      if (!result.user) {
        throw new Error('Échec de connexion');
      }

      // Vérifier si l'utilisateur a les droits d'administration
      const isAdmin = await this.checkIfUserIsAdmin(result.user.uid).pipe(take(1)).toPromise();
      
      if (!isAdmin) {
        // Déconnecter immédiatement si l'utilisateur n'est pas administrateur
        await this.afAuth.signOut();
        throw new Error('Vous n\'avez pas les droits d\'administration');
      }
      
      return true;
    } catch (error) {
      console.error('Erreur de connexion administrateur:', error);
      throw error;
    }
  }

  /**
   * Déconnecter l'administrateur
   */
  async adminLogout(): Promise<void> {
    try {
      await this.afAuth.signOut();
      this.adminUser.next(null);
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
      throw error;
    }
  }

  /**
   * Vérifier si l'utilisateur actuel est un administrateur
   */
  isAdmin(): boolean {
    return !!this.adminUser.value;
  }

  /**
   * Récupérer toutes les demandes de suppression de compte
   */
  getDeletionRequests(): Observable<DeletionRequest[]> {
    console.log('Démarrage de récupération des demandes de suppression');
    
    return this.db.collection<DeletionRequest>('accountDeletionRequests', 
      ref => ref.orderBy('createdAt', 'desc')
    ).snapshotChanges().pipe(
      map(actions => {
        console.log('Actions reçues de Firestore:', actions.length);
        return actions.map(a => {
          const data = a.payload.doc.data() as DeletionRequest;
          const id = a.payload.doc.id;
          return { ...data, id };
        });
      }),
      switchMap(requests => {
        console.log('Demandes avant enrichissement:', requests.length);
        // Récupérer les informations utilisateur pour chaque demande
        if (requests.length === 0) return of([]);
        
        const requestsWithUserInfo = requests.map(request => {
          return this.db.doc(`Users/${request.userId}`).get().pipe(
            map(userDoc => {
              if (userDoc.exists) {
                const userData = userDoc.data();
                console.log(`Infos utilisateur pour ${request.userId}:`, userData);
                return {
                  ...request,
                  userName: userData['publicName'] || 'Utilisateur inconnu',
                  userEmail: userData['email'] || 'Email non disponible',
                  userPhoneNumber: userData['zangiNumber'] || 'Téléphone non disponible'
                };
              }
              console.log(`Utilisateur ${request.userId} non trouvé`);
              return request;
            })
          );
        });
        
        return from(Promise.all(requestsWithUserInfo));
      }),
      tap(finalRequests => {
        console.log('Demandes finales récupérées:', finalRequests);
      }),
      catchError(error => {
        console.error('Erreur dans getDeletionRequests:', error);
        return of([]);
      })
    );
  }

  /**
   * Récupérer une demande de suppression spécifique
   */
  getDeletionRequest(requestId: string): Observable<DeletionRequest> {
    return this.db.doc<DeletionRequest>(`accountDeletionRequests/${requestId}`).valueChanges().pipe(
      map(request => {
        if (!request) throw new Error('Demande non trouvée');
        return { ...request, id: requestId };
      }),
      switchMap(request => {
        return this.db.doc(`Users/${request.userId}`).get().pipe(
          map(userDoc => {
            if (userDoc.exists) {
              const userData = userDoc.data();
              return {
                ...request,
                userName: userData['publicName'] || 'Utilisateur inconnu',
                userEmail: userData['email'] || 'Email non disponible',
                userPhoneNumber: userData['zangiNumber'] || 'Téléphone non disponible'
              };
            }
            return request;
          })
        );
      })
    );
  }

  /**
   * Mettre à jour le statut d'une demande
   */
  async updateRequestStatus(requestId: string, status: 'pending' | 'processing' | 'completed' | 'rejected', adminNote?: string): Promise<void> {
    try {
      await this.db.doc(`accountDeletionRequests/${requestId}`).update({
        status,
        adminNote: adminNote || '',
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedBy: this.adminUser.value?.email || 'unknown'
      });

      // Si le statut est "completed", déclencher la suppression du compte
      if (status === 'completed') {
        const request = await this.db.doc<DeletionRequest>(`accountDeletionRequests/${requestId}`).get().toPromise();
        if (request.exists) {
          const userId = request.data().userId;
          await this.accountDeletionService.deleteUserAccount(userId);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      throw error;
    }
  }

  /**
   * Obtenir des statistiques sur les demandes de suppression
   */
  getDeletionStats(): Observable<{
    pending: number;
    processing: number;
    completed: number;
    rejected: number;
    total: number;
  }> {
    return this.db.collection<DeletionRequest>('accountDeletionRequests').valueChanges().pipe(
      map(requests => {
        const stats = {
          pending: 0,
          processing: 0,
          completed: 0,
          rejected: 0,
          total: requests.length
        };

        requests.forEach(request => {
          stats[request.status]++;
        });

        return stats;
      })
    );
  }
}
